import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  HoldClashError,
  WEEKDAYS,
  asTime,
  clockValue,
  fetchMyHours,
  mayHold,
  saveMyHours,
  shapeOf,
  stretchesOn,
  type HoldChoice,
  type HoldClash,
  type HoursRow,
  type Weekday,
} from '../api/regularHours'
import { fetchDay, today } from '../api/calendar'
import { Plus, X } from 'lucide-react'
import InfoTip from './InfoTip'

/*
 * <b>Three chips rather than a dropdown</b> (D-719). `asked`: <i>"we may want to
 * cleanup the UI on how you enter the times that you are there some, its kinda
 * clunky"</i>. A select hid the three answers behind a tap on every row, and its
 * words ("I am in", "Not in") read as a sentence rather than a choice.
 */
const SHAPES: [value: 'unset' | 'hours' | 'closed', label: string][] = [
  ['unset', 'Not set'],
  ['hours', 'In'],
  ['closed', 'Out'],
]

/**
 * When a teacher is at the studio (D-674).
 *
 * **On her own account screen, beside the PIN and the payment link**, because it
 * is hers in the same way (D-320, D-662): nobody else's business to set, and the
 * studio holding it behind Tonya is what makes a change a text message.
 *
 * **Three states per day, and the third is what keeps the ask small.** All three
 * pilot items asked to *black out* time. An all-or-nothing positive model would
 * make a teacher who only wants Fridays off first enumerate every hour she is in
 * on the other four days — so a day she has not touched stays exactly as it was,
 * and she answers the question she actually had by changing one row.
 *
 * **Live the moment she saves** (D-709). The owner: *"plain availability is not
 * something to be approved by tonya, just teacher entering it."* So there is no
 * waiting state, and the cost is written into D-709: a week saved halfway
 * through a Tuesday narrows a family's grid at once.
 *
 * **Silent for a parent**, who has no staff row and nothing to set.
 */
export default function RegularHoursPanel({
  isStaff,
  isKiosk,
  canHoldRooms = false,
}: {
  isStaff: boolean
  /** A wall tablet is nobody in particular and sets nothing (D-233). */
  isKiosk: boolean
  /** She holds `BookRooms`, which a weekend room hold needs (D-711). */
  canHoldRooms?: boolean
}) {
  const enabled = isStaff && !isKiosk

  // Holding a room takes the same permission as booking one; the server refuses without it.
  const canHold = canHoldRooms

  const { data, isPending } = useQuery({
    queryKey: ['my-hours'],
    queryFn: fetchMyHours,
    enabled,
  })

  const [rows, setRows] = useState<HoursRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  /*
   * <b>Taken hold dates, and what she chose for each</b> (D-711). A save that
   * comes back with these wrote nothing; she picks another room or skips each
   * date, and the same Save goes again with her choices.
   */
  const [clashes, setClashes] = useState<HoldClash[]>([])
  const [choices, setChoices] = useState<HoldChoice[]>([])

  /*
   * The studio's rooms, for the hold picker. The day view already lists every
   * room, and a teacher may read it (D-368) - the admin room list she may not.
   */
  const rooms = useQuery({
    queryKey: ['hold-rooms'],
    queryFn: async () => (await fetchDay(today(), { wholeStudio: true })).rooms,
    enabled,
    staleTime: 5 * 60_000,
  })

  /*
   * The server is the source; the form follows it. It does nothing on a failed
   * save, deliberately — what she typed is still what she meant, and resetting
   * would lose her work to a network blip (the D-662 lesson, one panel over).
   */
  useEffect(() => {
    if (data) setRows(data.days)
  }, [data])

  const queryClient = useQueryClient()

  const save = useMutation({
    mutationFn: () => saveMyHours(rows, choices),
    onMutate: () => {
      setError(null)
      setSaved(false)
    },
    onSuccess: (result) => {
      setRows(result.days)
      setSaved(true)
      setClashes([])
      setChoices([])
      queryClient.invalidateQueries({ queryKey: ['my-hours'] })
    },
    onError: (e: Error) => {
      if (e instanceof HoldClashError) {
        setClashes(e.clashes)
        /*
         * Her earlier choice stays only while it can still be kept - a skip, or a
         * room still free. A room somebody took since she chose it would be sent
         * again and refused again, for ever, while the select showed another.
         */
        setChoices((prev) =>
          e.clashes.map((c) => {
            const earlier = prev.find((p) => sameDate(p, c))
            const stillGood = earlier && (earlier.roomId === null || c.freeRooms.some((r) => r.id === earlier.roomId))

            return stillGood ? earlier : { ...key(c), roomId: c.freeRooms[0]?.id ?? null }
          }),
        )
        return
      }

      setError(e.message)
    },
  })

  if (!enabled) return null

  /**
   * Every edit goes through here, so the confirmation cannot outlive the week it
   * describes — "Saved." sitting above a form she has since changed is a
   * sentence about something that is no longer true.
   */
  const change = (next: HoursRow[]) => {
    setSaved(false)
    setClashes([])
    setChoices([])
    setRows(next)
  }

  /**
   * A second stretch that does not collide with the first.
   *
   * <b>A fixed 09:00–12:00 duplicates the commonest row there is</b> — item 28's
   * own example is Saturdays 9 to 12 — and the server refuses a duplicate as an
   * overlap, which is a refusal she did nothing to earn. This starts an hour
   * after the last stretch ends instead.
   */
  const nextStretch = (stretches: HoursRow[], day: Weekday): HoursRow => {
    const last = stretches.at(-1)?.endTime

    if (!last) return { day, startTime: '09:00', endTime: '12:00' }

    const hour = Math.min(Number(last.slice(0, 2)) + 1, 22)
    const start = `${String(hour).padStart(2, '0')}:00`

    return { day, startTime: start, endTime: `${String(Math.min(hour + 2, 23)).padStart(2, '0')}:00` }
  }

  const setShape = (day: Weekday, shape: 'hours' | 'closed' | 'unset') => {
    setSaved(false)

    const others = rows.filter((r) => r.day !== day)

    if (shape === 'unset') return setRows(others)
    if (shape === 'closed') return setRows([...others, { day, startTime: null, endTime: null }])

    /*
     * One stretch, defaulted to the evening most of this studio teaches rather
     * than an empty box she has to fill twice.
     *
     * <b>It cannot preserve what she typed before</b>, and an earlier version
     * pretended it could: `setShape` only fires when the select changes, so by
     * the time it runs the day is already `closed` or `unset` and has no
     * stretches left to keep. The branch that looked like preservation was
     * dead, and the comment above it described a behaviour that never happened.
     * Switching to <i>Not in</i> and back is a real loss of her times — small,
     * but it should be true rather than quietly wrong.
     */
    setRows([...others, { day, startTime: '16:00', endTime: '20:30' }])
  }

  const setStretch = (day: Weekday, index: number, patch: Partial<HoursRow>) => {
    setSaved(false)
    setClashes([])
    setChoices([])

    const mine = stretchesOn(rows, day)
    const others = rows.filter((r) => r.day !== day)

    setRows([...others, ...mine.map((r, i) => (i === index ? { ...r, ...patch } : r))])
  }

  const field =
    'rounded-lg border border-line-2 bg-surface-2 px-2 py-1.5 text-[13px] outline-none focus:border-accent focus:bg-surface'

  return (
    <section className="rounded-2xl border border-line bg-surface px-4 py-4">
      <div className="flex flex-wrap items-center gap-1">
        <h2 className="text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
          When you are at the studio
        </h2>
        <InfoTip label="More about your hours">
          Families can only ask you for a private or a private makeup inside these times.
          <strong> Not set</strong> leaves a day open as it is now; <strong>Out</strong> means no
          requests that day. They run to the end of the season, and take effect when you save.
        </InfoTip>
      </div>

      <p className="mt-1 text-[13px] text-ink-2">Your usual week, for private requests.</p>

      {isPending ? (
        <p className="mt-3 text-[13px] text-ink-3">Loading...</p>
      ) : (
        <>
          <div className="mt-2 flex flex-col">
            {WEEKDAYS.map((day) => {
              const shape = shapeOf(rows, day)
              const stretches = stretchesOn(rows, day)

              return (
                <div
                  key={day}
                  className="flex flex-col gap-1.5 border-b border-line py-2 last:border-b-0"
                >
                  <div className="flex items-center gap-2">
                    <span className="w-[5.5rem] text-[13px] font-semibold text-ink-2">{day}</span>

                    <div
                      role="radiogroup"
                      aria-label={`${day} at the studio`}
                      className="inline-flex rounded-lg border border-line-2 bg-surface-2 p-0.5"
                    >
                      {SHAPES.map(([value, label]) => (
                        <label
                          key={value}
                          className={`flex min-h-[32px] cursor-pointer items-center rounded-md px-2.5 text-[12.5px] font-semibold has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent ${
                            shape === value ? 'bg-surface text-ink shadow-sm' : 'text-ink-3 hover:text-ink-2'
                          }`}
                        >
                          <input
                            type="radio"
                            name={`at-studio-${day}`}
                            value={value}
                            checked={shape === value}
                            onChange={() => setShape(day, value)}
                            className="sr-only"
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {shape === 'hours' && (
                    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                      {stretches.map((stretch, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-1.5">
                          <input
                            type="time"
                            value={clockValue(stretch.startTime)}
                            onChange={(e) => setStretch(day, i, { startTime: asTime(e.target.value) })}
                            aria-label={`${day} stretch ${i + 1} starts`}
                            className={field}
                          />
                          <span className="text-[12.5px] text-ink-3">&ndash;</span>
                          <input
                            type="time"
                            value={clockValue(stretch.endTime)}
                            onChange={(e) => setStretch(day, i, { endTime: asTime(e.target.value) })}
                            aria-label={`${day} stretch ${i + 1} ends`}
                            className={field}
                          />

                          {/*
                            <b>Also hold a room</b>, Friday to Sunday only
                            (D-710, D-712). The studio approves it once for the
                            season, and the room is held while it decides.
                          */}
                          {canHold && mayHold(day) && stretch.startTime !== null && (
                            <span className="flex flex-wrap items-center gap-1.5">
                              <label className="flex min-h-[36px] items-center gap-1.5 text-[12.5px] text-ink-2">
                                <input
                                  type="checkbox"
                                  checked={!!stretch.holdRoomId}
                                  onChange={(e) =>
                                    setStretch(day, i, {
                                      holdRoomId: e.target.checked ? (rooms.data?.[0]?.roomId ?? null) : null,
                                      holdState: null,
                                    })
                                  }
                                  disabled={!rooms.data?.length}
                                />
                                Also hold a room
                              </label>

                              {stretch.holdRoomId && (
                                <select
                                  value={stretch.holdRoomId}
                                  onChange={(e) => setStretch(day, i, { holdRoomId: e.target.value, holdState: null })}
                                  aria-label={`Room to hold on ${day} stretch ${i + 1}`}
                                  className={field}
                                >
                                  {(rooms.data ?? []).map((r) => (
                                    <option key={r.roomId} value={r.roomId}>
                                      {r.name}
                                    </option>
                                  ))}
                                </select>
                              )}

                              {stretch.holdRoomId && stretch.holdState === 'held' && (
                                <span className="text-[12px] font-semibold text-ink-2">Room held</span>
                              )}
                              {stretch.holdRoomId && stretch.holdState === 'waiting' && (
                                <span className="text-[12px] text-ink-3">Waiting on the studio</span>
                              )}
                            </span>
                          )}

                          {stretches.length > 1 && (
                            <button
                              type="button"
                              onClick={() =>
                                change([
                                  ...rows.filter((r) => r.day !== day),
                                  ...stretches.filter((_, j) => j !== i),
                                ])
                              }
                              aria-label={`Remove ${day} stretch ${i + 1}`}
                              title="Remove this time"
                              className="flex h-8 w-8 items-center justify-center rounded-md text-ink-3 hover:bg-surface-2 hover:text-crit"
                            >
                              <X size={15} aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      ))}

                      {/*
                        Morning and evening around another job is the ordinary
                        shape here, so a second stretch is one tap rather than a
                        thing she has to discover.
                      */}
                      <button
                        type="button"
                        onClick={() =>
                          change([
                            ...rows.filter((r) => r.day !== day),
                            ...stretches,
                            nextStretch(stretches, day),
                          ])
                        }
                        aria-label={`Add a time on ${day}`}
                        className="flex min-h-[32px] items-center gap-1 self-start text-[12.5px] font-semibold text-accent hover:underline"
                      >
                        <Plus size={14} aria-hidden="true" />
                        Add a time
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {error && (
            <p
              role="alert"
              className="mt-3 rounded-lg border border-crit bg-crit-bg px-3 py-2 text-[12.5px] text-crit"
            >
              {error}
            </p>
          )}

          {/*
            <b>What the write said, never what the last read said</b> (D-209):
            a refetch that never lands, which after a deploy is 75 seconds of
            every endpoint refusing (D-33), must not leave the panel describing
            the week before she pressed Save.

            <b>Live on save</b> (D-709), so the sentence says what families now
            see rather than who is deciding.
          */}
          {clashes.length > 0 && (
            <div role="alert" className="mt-3 rounded-lg border border-line-2 bg-surface-2 px-3 py-2.5">
              <p className="text-[12.5px] font-semibold text-ink">
                That room is taken on {clashes.length === 1 ? 'one date' : `${clashes.length} dates`}. Choose
                another room for each, or skip it. Nothing is saved yet.
              </p>

              <ul className="mt-2 flex flex-col gap-1.5">
                {clashes.map((c) => {
                  const chosen = choices.find((p) => sameDate(p, c))

                  return (
                    <li key={`${c.day}-${c.startTime}-${c.date}`} className="flex flex-wrap items-center gap-2">
                      <span className="text-[12.5px] text-ink-2">
                        {longDate(c.date)}, {clockValue(c.startTime)}&ndash;{clockValue(c.endTime)}
                      </span>
                      <select
                        value={chosen?.roomId ?? ''}
                        onChange={(e) =>
                          setChoices((prev) => [
                            ...prev.filter((p) => !sameDate(p, c)),
                            { ...key(c), roomId: e.target.value === '' ? null : e.target.value },
                          ])
                        }
                        aria-label={`Room for ${longDate(c.date)}`}
                        className={field}
                      >
                        {c.freeRooms.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                          </option>
                        ))}
                        <option value="">Skip this date</option>
                      </select>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {saved && save.data && (
            <p role="status" className="mt-3 text-[12.5px] text-ink-2">
              Saved. {save.data.days.length === 0
                ? 'You have not set any hours, so families can ask you exactly as they do now.'
                : 'Families can now ask you for a private or a makeup inside these times.'}
              {save.data.days.some((d) => d.holdState === 'waiting') &&
                ' Your room request has gone to the studio, and the room is held for you while it decides.'}
            </p>
          )}

          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              disabled={save.isPending}
              onClick={() => save.mutate()}
              className="rounded-lg bg-accent px-3 py-1.5 text-[13px] font-semibold text-on-accent hover:brightness-110 disabled:opacity-60"
            >
              {save.isPending ? 'Saving...' : 'Save'}
            </button>

            {data?.endsOn && (
              <span className="text-[11.5px] text-ink-3">
                These run to the end of the season and then stop.
              </span>
            )}
          </div>
        </>
      )}
    </section>
  )
}

/** The fields a choice and a clash share, to match one to the other. */
function key(c: HoldClash | HoldChoice) {
  return { day: c.day, startTime: c.startTime, endTime: c.endTime, date: c.date }
}

function sameDate(a: HoldClash | HoldChoice, b: HoldClash | HoldChoice): boolean {
  return a.day === b.day && a.startTime === b.startTime && a.endTime === b.endTime && a.date === b.date
}

/** "Saturday 10 October", from an ISO date, without a timezone shifting the day. */
function longDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  })
}
